import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import LogLayout from "../components/LogLayout";
import { AlertOctagon, Plus, Clock, CheckCircle2, Send, Loader2, MessageCircle, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const TYPES = [
  { value: "facturation", label: "Problème de facturation" },
  { value: "assureur", label: "Litige avec un assureur" },
  { value: "montant", label: "Erreur de montant" },
  { value: "document", label: "Document manquant" },
  { value: "technique", label: "Problème technique" },
  { value: "agent commercial", label: "Problème de livraison/agent commercial" },
  { value: "autre", label: "Autre" },
];

const STATUS_LABELS = {
  open: { label: "Ouvert", bg: "bg-amber-100 text-amber-700" },
  in_progress: { label: "En cours", bg: "bg-sky-100 text-sky-700" },
  resolved: { label: "Resolu", bg: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Ferme", bg: "bg-slate-100 text-slate-600" },
};

export default function LogSignalementPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ type: "facturation", subject: "", description: "", invoice_id: "" });
  const { user: authUser } = useAuth();
  const user = authUser || {};
  const isAdmin = user.role === "superadmin" || user.role === "admin";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/log/signalements`, { headers: getHeaders() });
      setReports(res.data.signalements || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Sujet requis"); return; }
    if (!form.description.trim()) { toast.error("Description requise"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/log/signalements`, form, { headers: getHeaders() });
      toast.success("Signalement cree avec succes");
      setShowForm(false);
      setForm({ type: "facturation", subject: "", description: "", invoice_id: "" });
      load();
    } catch (e) { toast.error("Erreur lors de la creation"); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/log/signalements/${id}/status`, { status: newStatus }, { headers: getHeaders() });
      toast.success("Statut mis a jour");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const stats = {
    total: reports.length,
    open: reports.filter(r => r.status === "open").length,
    in_progress: reports.filter(r => r.status === "in_progress").length,
    resolved: reports.filter(r => r.status === "resolved" || r.status === "closed").length,
  };

  if (loading) return (
    <LogLayout title="Signalement" subtitle="Signalez un probleme ou un dysfonctionnement">
      <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-500 border-t-transparent" /></div>
    </LogLayout>
  );

  return (
    <LogLayout title="Signalement" subtitle="Signalez un probleme et suivez sa resolution">
      <div className="space-y-5 max-w-4xl" data-testid="signalement-page">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-[10px] text-slate-500 uppercase">Total</p>
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-[10px] text-slate-500 uppercase">Ouverts</p>
            <p className="text-2xl font-bold text-amber-600">{stats.open}</p>
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-[10px] text-slate-500 uppercase">En cours</p>
            <p className="text-2xl font-bold text-sky-600">{stats.in_progress}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-[10px] text-slate-500 uppercase">Resolus</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.resolved}</p>
          </div>
        </div>

        {/* New Report Button */}
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-slate-700">Mes signalements</h3>
          <Button onClick={() => setShowForm(!showForm)} data-testid="new-signalement-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white text-xs gap-2">
            <Plus className="w-3.5 h-3.5" /> Nouveau signalement
          </Button>
        </div>

        {/* Create Form */}
        {showForm && (
          <div className="bg-white border border-sky-200 rounded-xl p-5 shadow-sm space-y-4" data-testid="signalement-form">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Type de signalement</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white">
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Sujet</label>
              <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                placeholder="Resumez le probleme en une phrase" className="text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">N° facture concernee (optionnel)</label>
              <Input value={form.invoice_id} onChange={e => setForm({ ...form, invoice_id: e.target.value })}
                placeholder="Ex: FACT-001" className="text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Description detaillee</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Decrivez le probleme en detail..." rows={4}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 resize-none" />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={submitting} data-testid="submit-signalement-btn"
                className="bg-sky-600 hover:bg-sky-500 text-white text-xs gap-2">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Envoyer
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs">Annuler</Button>
            </div>
          </div>
        )}

        {/* Reports List */}
        {reports.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
            <AlertOctagon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Aucun signalement pour le moment</p>
            <p className="text-xs text-slate-400 mt-1">Cliquez sur "Nouveau signalement" pour en creer un</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => {
              const st = STATUS_LABELS[r.status] || STATUS_LABELS.open;
              const isExpanded = expandedId === r.id;
              return (
                <div key={r.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`signalement-${r.id}`}>
                  <button onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50">
                    <AlertOctagon className={`w-4 h-4 flex-shrink-0 ${r.status === "open" ? "text-amber-500" : r.status === "in_progress" ? "text-sky-500" : "text-emerald-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.subject}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${st.bg}`}>{st.label}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        #{r.tracking_number} - {TYPES.find(t => t.value === r.type)?.label || r.type} - {new Date(r.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                      <p className="text-xs text-slate-600 leading-relaxed">{r.description}</p>
                      {r.invoice_id && <p className="text-[10px] text-slate-400">Facture: {r.invoice_id}</p>}
                      {r.response && (
                        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageCircle className="w-3 h-3 text-sky-500" />
                            <span className="text-[10px] font-bold text-sky-700">Reponse CMES</span>
                          </div>
                          <p className="text-xs text-slate-600">{r.response}</p>
                        </div>
                      )}
                      {isAdmin && r.status !== "closed" && (
                        <div className="flex gap-2 pt-2">
                          {r.status === "open" && (
                            <Button size="sm" onClick={() => updateStatus(r.id, "in_progress")} className="text-xs bg-sky-600 hover:bg-sky-500 text-white">Prendre en charge</Button>
                          )}
                          {(r.status === "open" || r.status === "in_progress") && (
                            <Button size="sm" onClick={() => updateStatus(r.id, "resolved")} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white">Marquer resolu</Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "closed")} className="text-xs">Fermer</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </LogLayout>
  );
}
